<template>
  <div class="relative flex w-full max-w-[210px]" :class="isExpanded ? 'justify-start' : 'justify-center -ml-4'">
    <ContextMenuWrapper class="max-w-full" test-id="sidebar-user" @is-visible="(val: boolean) => (toggleUserMenu = val)">
      <a
        @click.prevent="false"
        href="#"
        data-testid="sidebar-usermenu-trigger"
        class="max-w-fit flex items-center gap-2 rounded-full focus-visible:ring focus-within:ring-blue-500/50 focus:outline-none"
      >
        <Avatar :avatar-url="avatar" :name="translateInitials(name)" />
        <span v-if="isExpanded" class="text-sm font-bold dark:text-zinc-500 truncate">{{ name }}</span>
        <button v-if="isExpanded" :class="toggleUserMenu ? 'rotate-180' : ''" class="block w-4 h-4 duration-300 ease-in-out rounded-full focus:outline-none">
          <Icon name="general.chevronDown" size="xs" />
        </button>
      </a>
      <template #menu>
        <template v-for="item in menuItems" :key="item.name">
          <MenuModal v-if="item?.action?.type === 'modal'" :icon="item.icon" :type="item.type" :component="item.action.params.component">
            {{ item.title }}
          </MenuModal>

          <MenuLink v-else-if="item?.href" :href="item.href" :icon="item.icon" :type="item.type">
            {{ item.title }}
          </MenuLink>

          <MenuSeparator v-else-if="item?.isSeparator" />

          <MenuRouterLink v-else-if="item.to" :to="{ name: item.to }" :icon="item.icon" :type="item.type">
            {{ item.title }}
          </MenuRouterLink>
        </template>
        <template v-if="isSystemAdmin">
          <MenuSeparator />
          <MenuLink href="/admin" icon="general.cog" type="secondary"> Super Admin </MenuLink>
        </template>
        <MenuSeparator />
        <li class="relative group">
          <button class="theme-menu-trigger" aria-haspopup="true" :aria-expanded="isThemeOpen" @click.prevent.stop="isThemeOpen = !isThemeOpen">
            <span class="flex items-center gap-2">
              <Icon name="theme.auto" size="sm" class="text-blue-500" />
              Theme
            </span>
            <Icon name="general.nextItem" size="base" />
          </button>
          <ul v-show="isThemeOpen" class="theme-menu-submenu" :class="{ '!block': isThemeOpen }">
            <li>
              <button data-testid="dark-mode-light" class="theme-menu-item" @click.prevent.stop="colorMode = 'light'">
                <Icon :name="'theme.light'" size="sm" />
                <span>Light</span>
              </button>
            </li>
            <li>
              <button data-testid="dark-mode-dark" class="theme-menu-item" @click.prevent.stop="colorMode = 'dark'">
                <Icon :name="'theme.dark'" size="sm" />
                <span>Dark</span>
              </button>
            </li>
            <li>
              <button data-testid="dark-mode-auto" class="theme-menu-item" @click.prevent.stop="colorMode = 'auto'">
                <Icon :name="'theme.auto'" size="sm" />
                <span>System</span>
              </button>
            </li>
          </ul>
        </li>
      </template>
    </ContextMenuWrapper>
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useColorMode } from '@vueuse/core';
import { ContextMenuWrapper, Avatar, Icon, MenuLink, MenuRouterLink, MenuSeparator, MenuModal } from '@/storybook';

import { useNameInitials } from '@/composables/useNameInitials';
import { useUserMenu } from '@/composables/useUserMenu';
import { useUserStore } from '@/store/useUserStore';

defineProps<{
  isExpanded: boolean;
  avatar?: string;
  name?: string;
}>();

const { translateInitials } = useNameInitials();
const { menuItems } = useUserMenu();
const { isSystemAdmin } = storeToRefs(useUserStore());
const colorMode = useColorMode({ emitAuto: true, attribute: 'class', storage: localStorage });

const toggleUserMenu = ref(false);
const isThemeOpen = ref(false);
</script>
<style scoped>
.theme-menu-trigger {
  @apply flex w-full items-center justify-between gap-2 px-2 py-2 text-sm font-bold rounded-sm;
  @apply hover:bg-slate-100 dark:hover:bg-zinc-700 active:bg-slate-300;
  @apply dark:text-zinc-300 dark:hover:text-zinc-300;
  @apply focus-visible:ring focus-visible:ring-blue-500/50 focus-visible:bg-blue-500/10 focus:outline-none;
}

.theme-menu-submenu {
  @apply absolute z-50 hidden left-full top-0 -mt-2 p-2 w-max rounded-10;
  @apply bg-white border border-slate-300 shadow-lg dark:bg-zinc-900 dark:border-zinc-700;
  @apply group-hover:block group-focus-within:block;
}

.theme-menu-item {
  @apply flex w-full items-center gap-2 px-2 py-2 text-sm font-semibold rounded-sm dark:text-zinc-400 hover:bg-slate-100 dark:hover:bg-zinc-700;
}
</style>
